import { EARN_COINS } from './earnCatalog';

export const MAX_LEVEL = 10;

export const LEVEL_RANKS = Object.freeze([
  { range: '1–2', title: 'Guest' },
  { range: '3–4', title: 'Regular' },
  { range: '5–6', title: 'Local' },
  { range: '7–9', title: 'Host' },
  { range: '10', title: 'Legend' },
]);

export function xpForLevel(level) {
  const n = Math.max(1, Math.floor(Number(level) || 1));
  return 10 * (n - 1) * n;
}

export function titleForLevel(level) {
  const n = Math.max(1, Math.floor(Number(level) || 1));
  if (n >= 10) return 'Legend';
  if (n >= 7) return 'Host';
  if (n >= 5) return 'Local';
  if (n >= 3) return 'Regular';
  return 'Guest';
}

/** Legacy `true` uses the shipped rate. A stored number is the amount that was paid. */
function awardEntryXp(value, defaultCoins) {
  if (value === true) return defaultCoins;
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.floor(value));
  return 0;
}

function sumAwardXp(map, defaultCoins) {
  if (!map || typeof map !== 'object') return 0;
  return Object.values(map).reduce((sum, value) => sum + awardEntryXp(value, defaultCoins), 0);
}

/** Public / guest-safe XP from activity counters (approximate; shipped rates, no username / social). */
export function lifetimeXpFromStats(
  stats = {},
  {
    usernameAwarded = false,
    photoAwarded = false,
    bioAwarded = false,
    friendsAwarded = 0,
    firstMessagesAwarded = 0,
  } = {}
) {
  let xp =
    Math.max(0, Math.floor(Number(stats.eventsLiked) || 0)) * EARN_COINS.EVENT_SAVE +
    Math.max(0, Math.floor(Number(stats.placesLiked) || 0)) * EARN_COINS.PLACE_SAVE +
    Math.max(0, Math.floor(Number(stats.dropsCollected) || 0)) * EARN_COINS.DROP_PICKUP +
    Math.max(0, Math.floor(Number(stats.dropsMade) || 0)) * EARN_COINS.DROP_CREATE;
  if (usernameAwarded) xp += EARN_COINS.USERNAME;
  if (photoAwarded) xp += EARN_COINS.PHOTO;
  if (bioAwarded) xp += EARN_COINS.BIO;
  xp += Math.max(0, Math.floor(Number(friendsAwarded) || 0)) * EARN_COINS.FRIEND;
  xp += Math.max(0, Math.floor(Number(firstMessagesAwarded) || 0)) * EARN_COINS.FIRST_MESSAGE;
  return Math.max(0, Math.floor(xp));
}

/** Lifetime XP from idempotent award maps — unsaving never demotes. */
export function lifetimeXp(profile) {
  if (!profile) return 0;
  const awards = profile.mCoinAwards || {};
  const explore = profile.explorePointAwards || {};
  return (
    sumAwardXp(awards.likedEvents, EARN_COINS.EVENT_SAVE) +
    sumAwardXp(awards.likedLocations, EARN_COINS.PLACE_SAVE) +
    sumAwardXp(explore.pickups, EARN_COINS.DROP_PICKUP) +
    sumAwardXp(explore.creates, EARN_COINS.DROP_CREATE) +
    awardEntryXp(awards.username, EARN_COINS.USERNAME) +
    awardEntryXp(awards.photo, EARN_COINS.PHOTO) +
    awardEntryXp(awards.bio, EARN_COINS.BIO) +
    sumAwardXp(awards.friends, EARN_COINS.FRIEND) +
    sumAwardXp(awards.firstMessages, EARN_COINS.FIRST_MESSAGE)
  );
}

export function levelFromXp(xp) {
  const value = Math.max(0, Math.floor(Number(xp) || 0));
  let level = 1;
  while (value >= xpForLevel(level + 1) && level < MAX_LEVEL) {
    level += 1;
  }
  return level;
}

export function levelInfoFromXp(xp) {
  const value = Math.max(0, Math.floor(Number(xp) || 0));
  const level = levelFromXp(value);
  const currentAt = xpForLevel(level);
  const nextAt = level >= MAX_LEVEL ? currentAt : xpForLevel(level + 1);
  const span = Math.max(1, nextAt - currentAt);
  const progress =
    level >= MAX_LEVEL ? 1 : Math.max(0, Math.min(1, (value - currentAt) / span));
  return {
    level,
    title: titleForLevel(level),
    xp: value,
    currentAt,
    nextAt,
    progress,
  };
}

export function xpToNextLevel(info) {
  if (!info || info.level >= MAX_LEVEL) return 0;
  return Math.max(0, (Number(info.nextAt) || 0) - (Number(info.xp) || 0));
}

export function levelFromProfile(profile) {
  return levelInfoFromXp(lifetimeXp(profile));
}

export function levelFromPublicStats(stats) {
  return levelInfoFromXp(lifetimeXpFromStats(stats));
}

/** Preview rank after a 1:1 coin earn. */
export function levelAfterEarn(profile, earned) {
  const before = levelFromProfile(profile);
  const n = Math.max(0, Math.floor(Number(earned) || 0));
  const after = levelInfoFromXp(before.xp + n);
  return { before, after, leveledUp: after.level > before.level };
}
